import { type SupportedLocale } from '@zep-us/i18n';

import { type CategoryTreeBuildDto } from '@/api';
import { getSubjectLocaleName } from '@/shared/category/getSubjectLocaleName';
import { type GroupedSubjects } from '@/shared/category/type';
import { AllSchoolType } from '@/shared/category/util/SchoolMapper';

/**
 * 그룹화된 과목 정보를 생성하는 유틸 함수
 * 주어진 카테고리 트리 배열을 메인 과목 그룹별로 묶고,
 * 각 그룹의 하위 과목들을 children 객체로 정리합니다.
 */
export const groupBySubjectNodes = (
  subjects: CategoryTreeBuildDto[],
  locale: SupportedLocale,
) => {
  const grouped: GroupedSubjects = {};

  subjects.forEach(subject => {
    const mainSubjectGroupId = subject.categoryGroupId;

    // 메인 과목 그룹이 아직 등록되지 않았다면 초기화
    if (!grouped[mainSubjectGroupId]) {
      grouped[mainSubjectGroupId] = {
        name: getSubjectLocaleName({
          ...subject,
          defaultName: subject.name,
          locale,
        }),
        groupId: subject.categoryGroupId,
        children: {},
        order: subject.order,
      };
    }

    const children = subject.children;

    // 하위 과목들을 메인 그룹에 추가
    children.forEach(child => {
      // 'ALL' 일 경우는 상위 그룹 ID 사용, 그 외는 상하위 ID 조합
      const subSubjectGroupId =
        child.name === AllSchoolType.name
          ? subject.categoryGroupId
          : subject.categoryGroupId + child.categoryGroupId;

      grouped[mainSubjectGroupId].children[subSubjectGroupId] = {
        groupId: subSubjectGroupId,
        name: getSubjectLocaleName({
          ...child,
          defaultName: child.name,
          locale,
        }),
        order: child.order,
      };
    });
  });
  return grouped;
};

/**
 * 주어진 ID를 기반으로 과목 이름을 가져오는 getter 생성 함수
 * category 트리에서 subjectGroupId/subSubjectGroupId 조합으로 이름을 반환합니다.
 */
export const createSubjectNameGetter = (
  filteredCategories: CategoryTreeBuildDto[],
  locale: SupportedLocale,
) => {
  // 카테고리 트리를 그룹화하여 조회를 빠르게 함
  const subjectGroup = groupBySubjectNodes(filteredCategories, locale);

  // 반환되는 함수는 subjectGroupId, subSubjectGroupId로 이름을 조회
  return ({
    subjectGroupId,
    subSubjectGroupId,
  }: {
    subjectGroupId: string | undefined;
    subSubjectGroupId: string | undefined;
  }) => {
    if (Object.keys(subjectGroup).length === 0) return '';
    if (!subjectGroupId) return '';

    const subject = subjectGroup[subjectGroupId];
    if (!subject) return '';
    if (!subSubjectGroupId) return subject.name;
    // 하위 이름 반환, 없으면 빈 문자열
    return subject.children[subjectGroupId + subSubjectGroupId]?.name ?? '';
  };
};
